<template>
    <div>
        <v-container fluid>
            <v-card elevation="5">
                <v-toolbar flat dense class="elevation-5" height="40">
                    <v-toolbar-title class="title">
                        {{ $t("module.irgs_tools") }}
                    </v-toolbar-title>
                    <v-spacer/>

                    <v-btn
                            depressed
                            class="ma-2 white--text"
                            color="teal"
                            small
                            @click="dialog = true"
                    >
                        {{ $t("common.import") }}
                        <v-icon right dark>mdi-import</v-icon>
                    </v-btn>
                    <v-btn
                            depressed
                            class="white--text"
                            color="orange"
                            small
                            :loading="loading"
                            @click="verify"
                    >
                        Vérifier
                        <v-icon right>mdi-table-check</v-icon>
                    </v-btn>
                    <v-btn
                        depressed
                        class="ma-2 white--text"
                        color="blue darken-2"
                        small
                        :loading="loading"
                        @click="findAll"
                    >
                        {{ $t("common.reload") }}
                        <v-icon right dark>mdi-reload</v-icon>
                    </v-btn>
                </v-toolbar>
                <v-divider/>
                <v-container fluid>
                    <v-row dense>
                        <v-col cols="4">
                            <w-text-field
                                v-model="search"
                                :label="$t('common.search')"/>
                        </v-col>
                    </v-row>
                </v-container>
                <v-skeleton-loader
                        v-show="loading"
                        ref="skeleton"
                        :type="'table'"
                        :class="`mx-auto elevation-${tableElevation}`"
                ></v-skeleton-loader>
                <v-data-table
                        v-show="!loading"
                        :headers="headers"
                        :search="search"
                        :items="irgs"
                        :loading="loading"
                        dense
                        :class="`elevation-${tableElevation}`"
                        :page.sync="page"
                        :items-per-page="itemsPerPage"
                        hide-default-footer
                        @page-count="pageCount = $event"
                >
                    <template v-slot:[`item.base`]="{ item }">
                        <v-chip
                                class="white--text ma-1"
                                color="blue darken-2"
                                outlined
                                small
                        >
                            <strong>{{ priceFormat(item.base) }}</strong>
                        </v-chip>
                    </template>
                    <template v-slot:[`item.irg`]="{ item }">
                        <v-chip
                                outlined
                                label
                                small
                        >
                            <strong>{{ priceFormat(item.irg) }}</strong>
                        </v-chip>
                    </template>
                    <template v-slot:[`item.net`]="{ item }">
                        <v-chip
                                outlined
                                label
                                small
                        >
                            <strong>{{ priceFormat(item.net) }}</strong>
                        </v-chip>
                    </template>
                    <template v-slot:[`item.irgIndividuals`]="{ item }">
                        <v-chip
                                outlined
                                label
                                small
                        >
                            <strong>{{ priceFormat(item.irgIndividuals) }}</strong>
                        </v-chip>
                    </template>
                    <template v-slot:[`item.netIndividuals`]="{ item }">
                        <v-chip
                                outlined
                                label
                                small
                        >
                            <strong>{{ priceFormat(item.netIndividuals) }}</strong>
                        </v-chip>
                    </template>
                    <template v-slot:footer="{}">
                        <br/>
                        <div class="text-center">
                            <v-row justify="center">
                                <v-col md="6">
                                    <v-pagination
                                            v-model="page"
                                            :length="pageCount"
                                    ></v-pagination>
                                </v-col>
                                <v-col md="2">
                                    <w-enum-select
                                        v-model="itemsPerPage"
                                        :items="paginationListLength"
                                        :label="$t('common.items_per_page')"
                                    />
                                </v-col>
                            </v-row>
                        </div>
                    </template>
                </v-data-table>
            </v-card>
        </v-container>
        <v-dialog
                v-model="dialog"
                persistent
                max-width="700"
        >
            <v-card>
                <v-toolbar flat height="40" dark color="teal">
                    <v-toolbar-title>{{ $t('common.import') }}</v-toolbar-title>
                </v-toolbar>
                <v-card-text>
                    <v-container fluid>
                        <v-row dense>
                            <v-col cols="12">
                                <strong>{{ $t('common.file') }} *</strong>
                                <w-file-input
                                        v-model="file"
                                        :rules="[mxRules.required]"
                                />
                            </v-col>
                        </v-row>
                    </v-container>
                </v-card-text>
                <v-divider/>
                <v-card-actions>
                    <v-spacer/>
                    <v-btn
                            depressed
                            outlined
                            color="teal"
                            @click="dialog = false"
                    >
                        {{ $t('common.close') }}
                        <v-icon right>mdi-close</v-icon>
                    </v-btn>
                    <v-btn
                            depressed
                            class="white--text"
                            color="teal"
                            :loading="loading"
                            @click="upload"
                    >
                        {{ $t('common.import') }}
                        <v-icon right>mdi-import</v-icon>
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import {onMounted, ref} from "vue";
import useTools from "@/use/Tools";
import useValidation from "@/use/Validation";
import useSharedData from "@/use/SharedData";
import useSharedMethod from "@/use/SharedMethod";
import axios from "axios";

export default {
    name: "ImportIrg",
    setup() {
        const {mxRules} = useValidation();
        const {$swal} = useTools();
        const {priceFormat} = useSharedMethod();
        const {
            dialog,
            loading,
            tableElevation,
            page,
            pageCount,
            itemsPerPage,
            paginationListLength,
            inputColor,
            backgroundColor,
        } = useSharedData();
        dialog.value = false;

        const file = ref(null);
        const irgs = ref([]);
        const search = ref(null);

        const headers = [
            {text: 'Base Imposable', value: 'base'},
            {text: 'IRG (cas général)', value: 'irg'},
            {text: 'Net (cas général)', value: 'net'},
            {text: 'IRG (cas particuliers)', value: 'irgIndividuals'},
            {text: 'Net (cas particuliers)', value: 'netIndividuals'},
        ];

        function findAll() {
            loading.value = true;
            axios.get(`/irgs`).then(response => {
                irgs.value = response.data;
            }).finally(() => {
                loading.value = false;
            });
        }


        function upload() {
            loading.value = true;
            let formData = new FormData();
            formData.append('file', file.value);
            axios.post(`/irgs`, formData).then(response => {
                irgs.value = response.data;
                dialog.value = false;
            }).finally(() => {
                loading.value = false;
            });
        }

        function verify() {
            loading.value = true;
            axios.get(`/irgs/verify`).then(response => {
                if (response.data.length === 0) {
                    $swal.fire('Très Bien', "Le calcul de l'IRG est conforme aux normes standards", 'success')
                } else {
                    $swal.fire('Opps', "Veuillez vérifier le calcul de l'IRG", 'error')
                    irgs.value = response.data;
                }
            }).finally(() => {
                loading.value = false;
            });
        }

        onMounted(() => findAll());

        return {
            mxRules,
            dialog,
            loading,
            tableElevation,
            page,
            pageCount,
            itemsPerPage,
            paginationListLength,
            inputColor,
            backgroundColor,
            headers,
            file,
            search,
            irgs,
            priceFormat,
            verify,
            upload,
            findAll
        }
    }
}
</script>

<style scoped>

</style>